import { useEffect, useState } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { DEFAULT_EDITOR_SETTINGS } from '../editor/codemirrorConfig';

const AUTOCOMPLETE_MODES = ['off', 'manual', 'typing'];

function mergeSettings(fromProfile) {
  const base = { ...DEFAULT_EDITOR_SETTINGS, taskTextWrapping: true };
  if (!fromProfile || typeof fromProfile !== 'object') return base;
  const merged = { ...base, ...fromProfile };
  if (!AUTOCOMPLETE_MODES.includes(merged.autocompleteMode)) {
    merged.autocompleteMode = DEFAULT_EDITOR_SETTINGS.autocompleteMode;
  }
  return merged;
}

export default function EditorSettingsPage() {
  const { user, refreshUser } = useAuth();
  const [settings, setSettings] = useState(() => mergeSettings(user?.editor_settings));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setSettings(mergeSettings(user?.editor_settings));
  }, [user?.editor_settings]);

  function updateField(field, value) {
    setSettings((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.patch('/auth/profile/', { editor_settings: settings });
      await refreshUser?.();
      setSuccess('Настройки сохранены');
    } catch (e2) {
      const msg = e2?.response?.data;
      setError(typeof msg === 'string' ? msg : (msg?.detail || 'Не удалось сохранить настройки'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="profile-page">
      <div className="page-header">
        <h1>Настройки редактора</h1>
        <p className="page-subtitle">Применяются в редакторах кода и тексте задачи.</p>
      </div>

      <form className="profile-form" onSubmit={handleSave}>
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <div className="form-grid">
          <div className="form-group">
            <label>Размер текста (px)</label>
            <input
              type="number"
              className="form-input"
              min={10}
              max={24}
              value={settings.fontSizePx}
              onChange={(e) => updateField('fontSizePx', e.target.value ? Number(e.target.value) : DEFAULT_EDITOR_SETTINGS.fontSizePx)}
            />
          </div>

          <div className="form-group">
            <label>Интерлиньяж</label>
            <input
              type="number"
              className="form-input"
              min={1.1}
              max={2.2}
              step={0.05}
              value={settings.lineHeight}
              onChange={(e) => updateField('lineHeight', e.target.value ? Number(e.target.value) : DEFAULT_EDITOR_SETTINGS.lineHeight)}
            />
          </div>

          <div className="form-group">
            <label>Автодополнение</label>
            <select
              className="form-select"
              value={AUTOCOMPLETE_MODES.includes(settings.autocompleteMode) ? settings.autocompleteMode : 'off'}
              onChange={(e) => updateField('autocompleteMode', e.target.value)}
            >
              <option value="off">Выключено</option>
              <option value="manual">Только по Ctrl+Space</option>
              <option value="typing">Автоматически при наборе</option>
            </select>
            <p className="text-secondary" style={{ fontSize: '0.85rem', marginTop: '0.35rem', marginBottom: 0 }}>
              Всплывающий список шаблонов кода (например «if block») при наборе — это автодополнение, не подсказки к задаче.
            </p>
          </div>
        </div>

        <div className="form-group">
          <label>Опции</label>
          <div className="checkbox-row">
            <label><input type="checkbox" checked={!!settings.closeBrackets} onChange={(e) => updateField('closeBrackets', e.target.checked)} /> Автозакрытие скобок</label>
            <label><input type="checkbox" checked={!!settings.bracketMatching} onChange={(e) => updateField('bracketMatching', e.target.checked)} /> Подсветка парных скобок</label>
            <label><input type="checkbox" checked={!!settings.showHints} onChange={(e) => updateField('showHints', e.target.checked)} /> Подсказки к задаче (текст от автора)</label>
            <label><input type="checkbox" checked={!!settings.lineWrapping} onChange={(e) => updateField('lineWrapping', e.target.checked)} /> Перенос длинных строк</label>
            <label><input type="checkbox" checked={settings.taskTextWrapping !== false} onChange={(e) => updateField('taskTextWrapping', e.target.checked)} /> Перенос текста задачи</label>
            <label><input type="checkbox" checked={!!settings.lineNumbers} onChange={(e) => updateField('lineNumbers', e.target.checked)} /> Номера строк</label>
            <label><input type="checkbox" checked={!!settings.showSearch} onChange={(e) => updateField('showSearch', e.target.checked)} /> Поиск/замена (Ctrl+F / Ctrl+H)</label>
            <label><input type="checkbox" checked={!!settings.highlightSelectionMatches} onChange={(e) => updateField('highlightSelectionMatches', e.target.checked)} /> Подсветка всех вхождений</label>
            <label><input type="checkbox" checked={!!settings.highlightTrailingWhitespace} onChange={(e) => updateField('highlightTrailingWhitespace', e.target.checked)} /> Подсветка хвостовых пробелов</label>
            <label><input type="checkbox" checked={!!settings.highlightActiveLine} onChange={(e) => updateField('highlightActiveLine', e.target.checked)} /> Подсветка текущей строки</label>
            <label><input type="checkbox" checked={!!settings.rulersEnabled} onChange={(e) => updateField('rulersEnabled', e.target.checked)} /> Вертикальная линейка (80)</label>
            <label><input type="checkbox" checked={!!settings.indentGuides} onChange={(e) => updateField('indentGuides', e.target.checked)} /> Вертикальные направляющие (каждые 4 пробела)</label>
            <label><input type="checkbox" checked={settings.autosaveDrafts !== false} onChange={(e) => updateField('autosaveDrafts', e.target.checked)} /> Автосохранение черновика</label>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving || !user}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </form>
    </div>
  );
}

